import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PayoutItemStatus, type PayoutItemDto } from '@paladin-monorepo/common';
import { payoutsApi } from '@/lib/api/payouts';
import { Loading } from '@/components/Loading';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatDate';
import { getAppFormatLocale } from '@/utils/locale';

const payoutItemStatusVariant: Record<PayoutItemStatus, BadgeProps['variant']> = {
  PLANNED: 'warning',
  EXCLUDED: 'secondary',
  SENT_TO_PSP: 'info',
  PAID_OUT: 'success',
  FAILED: 'error',
};

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat(getAppFormatLocale(), { style: 'currency', currency }).format(amount);
}

export function PayoutsSettingsTab() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['payouts'],
    queryFn: () => payoutsApi.list(),
  });

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <p className="text-sm text-destructive">{t('settings.payouts.error')}</p>;
  }

  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('settings.payouts.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('settings.payouts.description')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('settings.payouts.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('settings.payouts.columns.project')}</TableHead>
              <TableHead>{t('settings.payouts.columns.status')}</TableHead>
              <TableHead className="text-right">{t('settings.payouts.columns.gross')}</TableHead>
              <TableHead className="text-right">{t('settings.payouts.columns.platformFee')}</TableHead>
              <TableHead className="text-right">{t('settings.payouts.columns.net')}</TableHead>
              <TableHead>{t('settings.payouts.columns.created')}</TableHead>
              <TableHead>{t('settings.payouts.columns.paid')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row: PayoutItemDto) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link
                    to={`/projects/${encodeURIComponent(row.projectId)}/workspace/payments`}
                    className="text-primary hover:underline"
                  >
                    {row.projectTitle || row.projectId}
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge variant={payoutItemStatusVariant[row.status]}>
                    {t(`settings.payouts.status.${row.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAmount(row.amountGross, row.currency)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAmount(row.platformFeeAmount, row.currency)}
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatAmount(row.amountNet, row.currency)}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDate(row.createdAt)}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {row.paidAt ? formatDate(row.paidAt) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
